/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type IOpenIDToken, type MatrixClient } from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";
import {
  type LivekitFocus,
  type MatrixRTCSession,
} from "matrix-js-sdk/lib/matrixrtc";
import { useEffect, useState } from "react";

import { useActiveLivekitFocus } from "../room/useActiveFocus";
import { useErrorBoundary } from "../useErrorBoundary";
import {
  FailToGetOpenIdToken,
  MatrixRTCFocusMissingError,
} from "../utils/errors";
import { doNetworkOperationWithRetry } from "../utils/matrix";
import { callTraceJournal, describeTraceError, safeOrigin } from "../telemetry";
import type { SFUCredentialsAdapter } from "../recovery/CallRecoveryAdapters";
import { throwIfAborted } from "../recovery/abort";

export interface SFUConfig {
  url: string;
  jwt: string;
}

export function sfuConfigEquals(a?: SFUConfig, b?: SFUConfig): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;

  return a.jwt === b.jwt && a.url === b.url;
}

// The bits we need from MatrixClient
export type OpenIDClientParts = Pick<
  MatrixClient,
  "getOpenIdToken" | "getDeviceId"
>;

type SFUCredentialsOperation = "openid" | "jwt";

/**
 * Ошибка одной операции credentials. Поля намеренно совместимы с Matrix/HTTP
 * classifier, а исходный объект всегда остаётся в cause.
 */
export class SFUCredentialsError extends Error {
  public readonly code?: string;
  public readonly status?: number;
  public readonly httpStatus?: number;
  public readonly errcode?: string;

  public constructor(
    public readonly operation: SFUCredentialsOperation,
    cause: unknown,
    options: {
      readonly httpStatus?: number;
      readonly errcode?: string;
      readonly code?: string;
    } = {},
  ) {
    super(
      operation === "openid"
        ? "Не удалось получить OpenID token"
        : "Не удалось получить SFU config",
      { cause },
    );
    this.name = "SFUCredentialsError";
    this.code = options.code;
    this.status = options.httpStatus;
    this.httpStatus = options.httpStatus;
    this.errcode = options.errcode;
  }
}

function getErrorFacts(error: unknown): {
  readonly httpStatus?: number;
  readonly errcode?: string;
} {
  if (typeof error !== "object" || error === null) return {};
  const record = error as Record<string, unknown>;
  const data =
    typeof record.data === "object" && record.data !== null
      ? (record.data as Record<string, unknown>)
      : undefined;
  const httpStatus =
    typeof record.httpStatus === "number"
      ? record.httpStatus
      : typeof record.status === "number"
        ? record.status
        : undefined;
  const errcode =
    typeof record.errcode === "string"
      ? record.errcode
      : typeof data?.errcode === "string"
        ? data.errcode
        : undefined;
  return { httpStatus, errcode };
}

export function useOpenIDSFU(
  client: OpenIDClientParts,
  rtcSession: MatrixRTCSession,
  onActiveCallError?: (error: unknown) => boolean,
  enabled = true,
): SFUConfig | undefined {
  const [sfuConfig, setSFUConfig] = useState<SFUConfig | undefined>(undefined);

  const activeFocus = useActiveLivekitFocus(rtcSession);
  const { showErrorBoundary } = useErrorBoundary();

  useEffect(() => {
    if (!enabled) {
      setSFUConfig(undefined);
    } else if (activeFocus) {
      getSFUConfigWithOpenID(client, activeFocus).then(
        (config) => {
          setSFUConfig(config);
        },
        (error: unknown) => {
          if (onActiveCallError?.(error)) {
            logger.error(
              "Не удалось обновить SFU config активного звонка",
              error,
            );
            return;
          }
          const boundaryError =
            error instanceof FailToGetOpenIdToken
              ? error
              : new FailToGetOpenIdToken(
                  error instanceof Error ? error : new Error("Unknown error"),
                );
          showErrorBoundary(boundaryError);
          logger.error("Failed to get SFU config", error);
        },
      );
    } else {
      setSFUConfig(undefined);
    }
  }, [client, activeFocus, onActiveCallError, showErrorBoundary, enabled]);

  return sfuConfig;
}

/**
 * Legacy normal-lifecycle facade: OpenID получает ограниченный retry,
 * а JWT выполняется ровно один раз и больше не скрывает ошибку.
 */
export async function getSFUConfigWithOpenID(
  client: OpenIDClientParts,
  activeFocus: LivekitFocus,
): Promise<SFUConfig> {
  const controller = new AbortController();
  try {
    return await getSFUConfig(
      client,
      activeFocus,
      controller.signal,
      async () =>
        await doNetworkOperationWithRetry(async () => client.getOpenIdToken()),
    );
  } catch (error) {
    if (error instanceof SFUCredentialsError && error.operation === "openid") {
      const cause =
        error.cause instanceof Error ? error.cause : new Error("Unknown error");
      throw new FailToGetOpenIdToken(cause);
    }
    throw error;
  }
}

/** Выполняет одну recovery-попытку OpenID + JWT с переданным AbortSignal. */
export async function getFreshSFUConfig(
  client: OpenIDClientParts,
  activeFocus: LivekitFocus,
  signal: AbortSignal,
): Promise<SFUConfig> {
  return getSFUConfig(client, activeFocus, signal, async () => {
    throwIfAborted(signal);
    try {
      const token = await client.getOpenIdToken();
      // Matrix SDK не принимает AbortSignal, поэтому поздний результат
      // отбрасывается на первой границе после await.
      throwIfAborted(signal);
      return token;
    } catch (error) {
      if (signal.aborted) throwIfAborted(signal);
      const facts = getErrorFacts(error);
      throw new SFUCredentialsError("openid", error, facts);
    }
  });
}

async function getSFUConfig(
  client: OpenIDClientParts,
  activeFocus: LivekitFocus,
  signal: AbortSignal,
  getOpenIdToken: () => Promise<IOpenIDToken>,
): Promise<SFUConfig> {
  throwIfAborted(signal);
  const openIdStartedAt = performance.now();
  const openIdEventId = callTraceJournal.record({
    eventType: "sfu.openid_started",
    stage: "sfu",
    outcome: "started",
  });

  let openIdToken: IOpenIDToken;
  try {
    openIdToken = await getOpenIdToken();
    throwIfAborted(signal);
  } catch (error) {
    callTraceJournal.record({
      eventType: "sfu.openid_failed",
      stage: "sfu",
      severity: "error",
      outcome: "failed",
      relatedEventId: openIdEventId,
      durationMs: Math.round(performance.now() - openIdStartedAt),
      payload: describeTraceError(error),
    });
    if (error instanceof DOMException && error.name === "AbortError")
      throw error;
    if (error instanceof SFUCredentialsError) throw error;
    const facts = getErrorFacts(error);
    throw new SFUCredentialsError("openid", error, facts);
  }
  callTraceJournal.record({
    eventType: "sfu.openid_succeeded",
    stage: "sfu",
    outcome: "succeeded",
    relatedEventId: openIdEventId,
    durationMs: Math.round(performance.now() - openIdStartedAt),
  });

  const configStartedAt = performance.now();
  const configEventId = callTraceJournal.record({
    eventType: "sfu.config_fetch_started",
    stage: "sfu",
    outcome: "started",
    payload: { focusOrigin: safeOrigin(activeFocus.livekit_service_url) },
  });
  try {
    const config = await getLiveKitJWT(
      client,
      activeFocus.livekit_service_url,
      activeFocus.livekit_alias,
      openIdToken,
      signal,
    );
    callTraceJournal.record({
      eventType: "sfu.config_fetch_succeeded",
      stage: "sfu",
      outcome: "succeeded",
      relatedEventId: configEventId,
      durationMs: Math.round(performance.now() - configStartedAt),
      payload: { livekitOrigin: safeOrigin(config.url) },
    });
    return config;
  } catch (error) {
    callTraceJournal.record({
      eventType: "sfu.config_fetch_failed",
      stage: "sfu",
      severity: "error",
      outcome: "failed",
      reasonCode: "sfu_config_fetch_failed",
      relatedEventId: configEventId,
      durationMs: Math.round(performance.now() - configStartedAt),
      payload: describeTraceError(error),
    });
    throw error;
  }
}

async function readSFUErrorResponse(response: Response): Promise<{
  readonly errcode?: string;
}> {
  try {
    const body: unknown = await response.json();
    if (typeof body !== "object" || body === null) return {};
    const errcode = (body as Record<string, unknown>).errcode;
    return typeof errcode === "string" ? { errcode } : {};
  } catch {
    return {};
  }
}

/** Выполняет единственный отменяемый HTTP-запрос к SFU auth service. */
export async function getLiveKitJWT(
  client: OpenIDClientParts,
  livekitServiceURL: string,
  roomName: string,
  openIDToken: IOpenIDToken,
  signal: AbortSignal,
): Promise<SFUConfig> {
  throwIfAborted(signal);
  let response: Response;
  try {
    response = await fetch(livekitServiceURL + "/sfu/get", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        room: roomName,
        openid_token: openIDToken,
        device_id: client.getDeviceId(),
      }),
      signal,
    });
  } catch (error) {
    if (signal.aborted) throwIfAborted(signal);
    throw new SFUCredentialsError("jwt", error);
  }
  throwIfAborted(signal);

  if (!response.ok) {
    const { errcode } = await readSFUErrorResponse(response);
    throwIfAborted(signal);
    throw new SFUCredentialsError("jwt", response, {
      httpStatus: response.status,
      errcode,
      // Известные Element Call codes classifier распознает как явный
      // server terminal; неизвестный errcode останется обычным кодом ошибки.
      code: errcode,
    });
  }

  try {
    const body: unknown = await response.json();
    throwIfAborted(signal);
    if (
      typeof body !== "object" ||
      body === null ||
      typeof (body as Record<string, unknown>).url !== "string" ||
      typeof (body as Record<string, unknown>).jwt !== "string"
    ) {
      throw Object.assign(new TypeError("SFU config имеет неверный формат"), {
        code: "SFU_ERROR",
      });
    }
    return {
      url: (body as Record<string, string>).url,
      jwt: (body as Record<string, string>).jwt,
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw error;
    if (error instanceof SFUCredentialsError) throw error;
    throw new SFUCredentialsError("jwt", error, {
      httpStatus: response.status,
    });
  }
}

/** Создаёт recovery adapter, который заново читает active focus на каждый вызов. */
export function createSFUCredentialsAdapter(
  client: OpenIDClientParts,
  getActiveFocus: () => LivekitFocus | undefined,
  domain = "",
): SFUCredentialsAdapter {
  return {
    async getFresh(signal): Promise<SFUConfig> {
      throwIfAborted(signal);
      const focus = getActiveFocus();
      if (!focus) throw new MatrixRTCFocusMissingError(domain);
      return getFreshSFUConfig(client, focus, signal);
    },
  };
}
